import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { ShieldAlert, Loader2 } from 'lucide-react';
import { acceptLiabilityWaiver } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const LiabilityWaiverModal = ({ open, onAccept, onClose }) => {
  const { refreshUser } = useAuth();
  const scrollRef = useRef(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset when opened
  useEffect(() => {
    if (open) {
      setScrolledToEnd(false);
      setAgreed(false);
      // give the dialog a frame to render
      setTimeout(() => {
        const el = scrollRef.current;
        if (el && el.scrollHeight <= el.clientHeight + 4) {
          // Content fully visible without scrolling, treat as scrolled
          setScrolledToEnd(true);
        }
      }, 50);
    }
  }, [open]);

  const handleScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 24) {
      setScrolledToEnd(true);
    }
  };

  const handleAccept = useCallback(async () => {
    if (!scrolledToEnd || !agreed) return;
    setSubmitting(true);
    try {
      await acceptLiabilityWaiver();
      await refreshUser();
      toast.success('Waiver accepted');
      onAccept?.();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || 'Could not record acceptance';
      toast.error(typeof msg === 'string' ? msg : 'Could not record acceptance');
    } finally {
      setSubmitting(false);
    }
  }, [scrolledToEnd, agreed, onAccept, refreshUser]);

  const canSubmit = scrolledToEnd && agreed && !submitting;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="bg-[#0d0d10] border-zinc-800 max-w-2xl text-white"
        data-testid="liability-waiver-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-center">
              <ShieldAlert className="w-5 h-5 text-red-400" />
            </div>
            <DialogTitle className="text-white text-xl">Liability Waiver &amp; Assumption of Risk</DialogTitle>
          </div>
          <DialogDescription className="text-zinc-400 text-sm">
            Please read carefully. You must scroll to the bottom and check the box below to continue.
          </DialogDescription>
        </DialogHeader>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="max-h-[50vh] overflow-y-auto pr-4 -mr-4 text-sm text-zinc-300 leading-relaxed space-y-4 border border-zinc-800 rounded-md p-4 bg-[#09090B]"
          data-testid="waiver-scroll"
        >
          <p className="font-semibold text-white">1. Assumption of Risk</p>
          <p>
            I acknowledge that physical training carries inherent and substantial risks. These risks include,
            without limitation: muscle strains and tears, ligament and tendon damage, broken bones and
            fractures, joint damage and dislocation, concussions and other head injuries, internal injury,
            heart attack, stroke, paralysis, and in extreme cases <strong>death</strong>. I voluntarily
            assume all such risks, known and unknown, by booking any session through DojoDash.
          </p>

          <p className="font-semibold text-white">2. Elevated Risk for Self-Defense and Brazilian Jiu-Jitsu</p>
          <p>
            I specifically acknowledge that Self-Defense and Brazilian Jiu-Jitsu sessions are
            <strong> contact-sport activities</strong> with a <strong>materially elevated risk</strong> of
            joint injury (including knees, shoulders, elbows, fingers, and neck), impact injury,
            asphyxiation due to chokes performed for instructional purposes, and other physical harm. I
            understand these risks and voluntarily accept them.
          </p>

          <p className="font-semibold text-white">3. Medical Clearance</p>
          <p>
            I represent and warrant that I am medically cleared to participate in vigorous physical
            activity. I have consulted, or have had the opportunity to consult, with a physician about my
            fitness to train and have determined I am fit to do so. I will immediately stop any session if
            I experience pain, dizziness, shortness of breath, or any other symptom that suggests injury or
            medical emergency.
          </p>

          <p className="font-semibold text-white">4. Release of Liability</p>
          <p>
            To the fullest extent permitted by law, I hereby <strong>release, waive, and forever discharge</strong>
            DojoDash, its founders, officers, employees, contractors, agents, AND the individual Trainer
            with whom I book sessions, from any and all claims, demands, damages, causes of action,
            present or future, whether known or unknown, anticipated or unanticipated, arising out of or
            related to any injury, illness, disability, death, or property loss I may sustain as a result
            of participating in any session booked through the platform.
          </p>

          <p className="font-semibold text-white">5. No Verification of Credentials</p>
          <p>
            I acknowledge that DojoDash is a <strong>technology marketplace only</strong>. DojoDash does
            not verify trainer certifications, credentials, methods, or qualifications. DojoDash does not
            supervise, attend, or control any session. Trainers are independent contractors, not employees
            or agents of DojoDash. I am solely responsible for evaluating any trainer before booking.
          </p>

          <p className="font-semibold text-white">6. Indemnification</p>
          <p>
            I agree to indemnify and hold harmless DojoDash and its representatives from any claim or
            demand brought by myself, my family, my estate, my heirs, or any third party arising out of my
            participation in any session.
          </p>

          <p className="font-semibold text-white">7. Severability and Governing Law</p>
          <p>
            If any provision of this waiver is held to be invalid or unenforceable, the remaining
            provisions shall remain in full force and effect. This waiver is governed by the laws of the
            State of Florida.
          </p>

          <p className="font-semibold text-white">8. Acknowledgment</p>
          <p>
            I HAVE READ THIS WAIVER IN ITS ENTIRETY. I UNDERSTAND IT IS A RELEASE OF LIABILITY AND
            ASSUMPTION OF RISK. I SIGN IT VOLUNTARILY AND OF MY OWN FREE WILL.
          </p>
          <p className="text-zinc-500 text-xs pt-2">,  End of waiver , </p>
        </div>

        <div className="mt-4 flex items-start gap-3">
          <Checkbox
            id="waiver-agree"
            checked={agreed}
            onCheckedChange={(v) => setAgreed(!!v)}
            disabled={!scrolledToEnd}
            data-testid="waiver-agree-checkbox"
            className="mt-0.5 border-zinc-600 data-[state=checked]:bg-[#C0A062] data-[state=checked]:border-[#C0A062]"
          />
          <label htmlFor="waiver-agree" className={`text-sm leading-relaxed ${scrolledToEnd ? 'text-zinc-200' : 'text-zinc-500'}`}>
            I have read and understood the above. I voluntarily accept all risks and release DojoDash and
            its trainers from liability to the fullest extent permitted by law.
          </label>
        </div>

        {!scrolledToEnd && (
          <p className="text-xs text-amber-400 mt-2" data-testid="waiver-scroll-hint">
            Scroll to the bottom of the waiver to enable the checkbox.
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-zinc-400 hover:text-white"
            data-testid="waiver-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAccept}
            disabled={!canSubmit}
            className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold disabled:opacity-40"
            data-testid="waiver-accept-btn"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            I Accept &amp; Continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LiabilityWaiverModal;
